import { useCallback } from "react";

import { ApiScraperLoginStatusChoices } from "@api/graphql/types-and-hooks";
import { useAdminData } from "@providers/AdminDataProvider";
import { useAnalytics } from "@providers/AnalyticsProvider";

export function useEhrAnalytics() {
  const analytics = useAnalytics();
  const { orgId, orgName } = useAdminData();

  const captureGoalToggled = useCallback(
    (goal_id: string, checked: boolean) =>
      analytics?.capture("ehr_goal_toggled", { goal_id, checked, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureGoalExpanded = useCallback(
    (goal_id: string, expanded: boolean) =>
      analytics?.capture("ehr_goal_expanded", { goal_id, expanded, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureGoalsUpdated = useCallback(
    (goal_count: number, target_count: number) =>
      analytics?.capture("ehr_goals_updated", { goal_count, target_count, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrConnected = useCallback(
    (outcome: "existing" | "fresh") =>
      analytics?.capture("EHR connected", { outcome, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrLoginFailed = useCallback(
    // "mfa_relay_error" covers a real failure submitting/relaying the MFA code
    // itself (network/GraphQL error, or an unexpected relay response), and
    // "saved_login_relay_error" the equivalent for the saved-credential
    // auto-login relay — both distinct from any ApiScraperLoginStatusChoices
    // value since no polled status exists yet (or is stale) at that point and
    // would misrepresent the actual cause.
    (reason: ApiScraperLoginStatusChoices | "mfa_relay_error" | "saved_login_relay_error" | null | undefined) =>
      analytics?.capture("Failed EHR connection", { reason: reason ?? "unknown", org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrDisconnected = useCallback(
    () => analytics?.capture("EHR disconnected", { org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrBadgeClicked = useCallback(
    () => analytics?.capture("ehr_badge_clicked", { org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrLoginModalOpened = useCallback(
    () => analytics?.capture("ehr_login_modal_opened", { org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrLoginSubmitted = useCallback(
    () => analytics?.capture("ehr_login_submitted", { org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrDisconnectClicked = useCallback(
    () => analytics?.capture("ehr_disconnect_clicked", { org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  const captureEhrRefreshClicked = useCallback(
    (
      source:
        | "clients_page"
        | "new_report_modal"
        | "report_goals_step"
        | "select_programming_empty"
        | "ehr_data_programming",
    ) => analytics?.capture("ehr_refresh_clicked", { source, org_id: orgId, org_name: orgName }),
    [analytics, orgId, orgName],
  );

  return {
    captureGoalToggled,
    captureGoalExpanded,
    captureGoalsUpdated,
    captureEhrConnected,
    captureEhrLoginFailed,
    captureEhrDisconnected,
    captureEhrBadgeClicked,
    captureEhrLoginModalOpened,
    captureEhrLoginSubmitted,
    captureEhrDisconnectClicked,
    captureEhrRefreshClicked,
  };
}
